import { xcss, Box, Inline, Stack, Text } from '@atlaskit/primitives';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge/new';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInfo, useToken } from '../../../../../Flow/ConsentContextProvider';
import { MCP_CUSTOM_AGENT_SUPPORT_URL } from '../../../../MCP-optional-sites/mcpUrls'
import { getForgeMcpAppName } from '#definitions/consent-info';
import { headlineMessages } from '../messages';

const headlineTextStyles = xcss({
  margin: 'space.0',
  font: 'font.heading.large',
  color: 'color.text',
});

interface HeaderProps {
  product?: string;
  agentName?: string;
  agentDescription?: string;
}

/** The headline names the agent, then the Forge app, else the product. */
function getTitleMessage(agentName?: string, forgeAppName?: string) {
  if (agentName) return headlineMessages.agentTitle;
  if (forgeAppName) return headlineMessages.forgeAppTitle;
  return headlineMessages.title;
}

const Header = ({
  product = 'Atlassian',
  agentName,
  agentDescription,
}: HeaderProps) => {
  const { formatMessage } = useIntl();
  const { clientName } = useToken();
  const info = useInfo();
  const forgeAppName = getForgeMcpAppName(info);
  const isAgentHeader = !!agentName;

  return (
    <>
      <Stack space="space.100" grow="hug">
        {isAgentHeader && (
          <Inline alignBlock="center" space="space.100">
            <Lozenge appearance="neutral">
              <FormattedMessage {...headlineMessages.customAgentName} />
            </Lozenge>
            <Tooltip position="bottom-start" content={(
              <FormattedMessage
                {...headlineMessages.customAgentTooltip}
                values={{
                  learnMoreLink: (
                    <Link href={MCP_CUSTOM_AGENT_SUPPORT_URL}>
                      <FormattedMessage {...headlineMessages.customAgentLearnMoreLink} />
                    </Link>
                  )
                }}
              />
            )}>
              <InformationCircleIcon label={formatMessage(headlineMessages.customAgentInfoIconLabel)} />
            </Tooltip>
          </Inline>
        )}
        <Box as="h1" xcss={headlineTextStyles}>
          <FormattedMessage
            {...getTitleMessage(agentName, forgeAppName)}
            values={{
              requestingParty: clientName,
              product,
              appName: forgeAppName,
              agentName,
            }}
          />
        </Box>
      </Stack>
      {isAgentHeader && agentDescription ? (
        <Text as="p">{agentDescription}</Text>
      ) : null}
    </>
  );
};

export default Header;
